import { createSlice, createAsyncThunk, createAction } from '@reduxjs/toolkit';
import parsePhoneNumber from 'libphonenumber-js';

import countries from 'utils/countries.json';
import { escapedCountryField } from 'utils/transform';
import { rejectValue } from 'redux/helpers';
import { showErrorMessage, showSuccessMessage } from '../notification';
import { getStoreById } from '../javelinStore';
import { saveShopperDetails } from '../checkoutForm';
import { cartProductsSelector, cleanCart } from '../cart';
import { getOrder } from '../order';

import {
  POST_STORE_SHOPPER,
  GET_STORE_SHOPPER,
  POST_VERIFY_CHECKOUT_STORE,
  POST_VERIFY_BY_CODE,
  POST_DELIVERY_ADDRESS,
  GET_DELIVERY_ADDRESS,
  POST_SHOPPER_LOGOUT,
  POST_CHECKOUT_PAYMENT_METHODS,
  POST_ORDER,
  POST_PREVIEW_STRIPE_TERMINAL_ORDER,
} from 'services/api';
import {
  CHECKOUT,
  CHECKOUT_ADD_SHOPPER_DETAILS,
  CHECKOUT_GET_SHOPPER_DETAILS,
  CHECKOUT_GET_VERIFICATION_CODE,
  CHECKOUT_VALIDATE_VERIFICATION_CODE,
  CHECKOUT_ADD_DELIVERY_ADDRESS,
  CHECKOUT_GET_DELIVERY_ADDRESS,
  CHECKOUT_CHANGE_SHOPPER,
  CHECKOUT_PAYMENT_METHODS,
  CHECKOUT_ORDER_CREATE,
  CHECKOUT_PREVIEW_STRIPE_TERMINAL_ORDER
} from './types';
import { paymentMethod as PAYMENT_TEXT } from 'locales/en/cart';
import { logEvent } from 'utils/ga';
import { FIELD_NAMES as BILLING_ADDRESS_FIELD_NAMES } from 'pages/Checkout/components/BillingAddress/BillingAddress.schema';

export const initialState = {
  loading: false,
  checkoutStep: 1,
  shopperDetails: {},
  verificationCodeId: null,
  shippingDetails: {},
  checkoutPaymentMethods: [],
  orderCreateResponse: {},
  store: {},
  error: null,
  url: '',
  shippingAddressDisabled: false,
  canPreviewStripeTerminalOrder: false,
  stripeTerminalPreview: null,
  previewingTerminalOrder: false,
  renderStripeForm: false,
};

export const addShopperDetails = createAsyncThunk(
  CHECKOUT_ADD_SHOPPER_DETAILS,
  async (payload, { rejectWithValue, getState, dispatch }) => {
    try {
      const { store_id, ..._payload } = payload;
      const data = await POST_STORE_SHOPPER(_payload);
      const cart = getState().cart;

      logEvent('add_shopper_info', {
        cart_product_ids: cart.ids?.join(';') || '',
        total: cart.total,
        currency: 'EUR',
      });

      return data;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);
export const getShopperDetails = createAsyncThunk(
  CHECKOUT_GET_SHOPPER_DETAILS,
  async ({ storeId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const data = await GET_STORE_SHOPPER();
      if (data.phone_number) {
        const parsedPhone = parsePhoneNumber(data.phone_number);
        if (!getState().checkoutForm.shopperDetails.name) {
          dispatch(
            saveShopperDetails({
              name: `${data.name} ${data.surname}`,
              phone_country: countries.find(
                (item) =>
                  item.dial_code === `+${parsedPhone.countryCallingCode}`
              ),
              phone: parsedPhone.nationalNumber,
              email: data.email,
            })
          );
        }
      }
      return data;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);
export const changeShopper = createAsyncThunk(
  CHECKOUT_CHANGE_SHOPPER,
  async (_, { rejectWithValue, dispatch, getState }) => {
    const cart = getState().cart;
    logEvent('change_shopper_info', {
      cart_product_ids: cart.ids?.join(';') || '',
      total: cart.total,
      currency: 'EUR',
    });
    try {
      const { data } = await POST_SHOPPER_LOGOUT();
      return data;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const requestVerificationCode = createAsyncThunk(
  CHECKOUT_GET_VERIFICATION_CODE,
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await POST_VERIFY_CHECKOUT_STORE(payload);
      return data.id;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);
export const validateVerificationCode = createAsyncThunk(
  CHECKOUT_VALIDATE_VERIFICATION_CODE,
  async (payload, { rejectWithValue, dispatch, getState }) => {
    try {
      const verificationCodeId = getState().checkout.verificationCodeId;
      if (!verificationCodeId) {
        dispatch(setCheckoutStep(1));
        return Promise.reject();
      }

      const { data } = await POST_VERIFY_BY_CODE(payload, verificationCodeId);
      dispatch(getDeliveryAddress());
      return data;
    } catch (error) {
      if (!!error.response?.data?.message)
        dispatch(showErrorMessage({ message: error.response.data.message }));
      return rejectWithValue(rejectValue(error.response));
    }
  }
);
export const addDeliveryAddress = createAsyncThunk(
  CHECKOUT_ADD_DELIVERY_ADDRESS,
  async (payload, { rejectWithValue, getState, dispatch }) => {
    try {
      const phone_number = getState().checkout.shopperDetails.phone_number;
      const { data } = await POST_DELIVERY_ADDRESS({
        ...payload,
        phone_number,
      });
      dispatch(showSuccessMessage({ message: 'Shipping details saved!' }));
      return data;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);
export const getDeliveryAddress = createAsyncThunk(
  CHECKOUT_GET_DELIVERY_ADDRESS,
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      const { data } = await GET_DELIVERY_ADDRESS();
      return data;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);
export const getCheckoutPaymentMethods = createAsyncThunk(
  CHECKOUT_PAYMENT_METHODS,
  async ({ storeId }, { rejectWithValue, getState }) => {
    try {
      const _storeId = storeId || getState().checkout.store.id;
      const data = await POST_CHECKOUT_PAYMENT_METHODS(_storeId);
      return data;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const createOrder = createAsyncThunk(
  CHECKOUT_ORDER_CREATE,
  async ({ storeId, adyenData }, { rejectWithValue, getState, dispatch }) => {
    const { shopperDetails } = getState().checkout;
    const { billingOption, ..._billingAddress } =
      getState().checkoutForm.billingAddress;
    const billing_address = {
      ..._billingAddress,
      phone_number: shopperDetails.phone_number,
      country: _billingAddress[escapedCountryField].name,
      city: '-',
    };
    const products = cartProductsSelector(getState());
    const cart = getState().cart;

    try {
      const { data } = await POST_ORDER({
        adyen_data: adyenData,
        products,
        name: shopperDetails.name,
        surname: shopperDetails.surname,
        comments: cart.comments,
        store_id: storeId,
        billing_address,
        user_pays_fees: cart.userPaysFees,
        is_anonymous: cart.isAnonymous ? 1 : 0,
      });

      const items = cart.ids.map((item) => ({
        item_id: item,
        item_name: cart.entities[item].product.name,
        price: cart.entities[item].price,
        product_id: cart.entities[item].product_id,
        quantity: cart.entities[item].quantity,
      }));

      logEvent('order_create', {
        cart_product_ids: cart.ids?.join(';') || '',
        value: cart.charge,
        total: cart.total,
        status: data.status,
        transaction_id: data.transaction_id,
        currency: 'EUR',
        payment_type:
          adyenData.paymentMethod.type === 'scheme'
            ? 'card'
            : adyenData.paymentMethod.type,
        items,
      });

      return data;
    } catch (error) {
      return rejectWithValue(
        rejectValue({ ...error.response, skipNotification: true })
      );
    }
  }
);

export const previewStripeTerminalOrder = createAsyncThunk(
  CHECKOUT_PREVIEW_STRIPE_TERMINAL_ORDER,
  async ({ store: storeId, form }, { rejectWithValue, getState, dispatch }) => {

    const { shopperDetails, billingAddress } = getState().checkoutForm;
    const { entities } = getState().cart;
    const products = Object.entries(entities).map(([cartId, prod]) => ({
      ...prod.product, quantity: prod.quantity, type: prod.quantity
    }));

    const phoneNumber = shopperDetails?.phone_country?.dial_code + shopperDetails?.phone;
    const { comments, isAnonymous, userPaysFees } = getState().cart;

    const _address = {
      recipient_name: form?.[BILLING_ADDRESS_FIELD_NAMES.name] || billingAddress?.[BILLING_ADDRESS_FIELD_NAMES.name],
      phone_number: phoneNumber,
      address_1: form?.[BILLING_ADDRESS_FIELD_NAMES.address_1] || billingAddress?.[BILLING_ADDRESS_FIELD_NAMES.address_1],
      address_2: form?.[BILLING_ADDRESS_FIELD_NAMES.address_2] || billingAddress?.[BILLING_ADDRESS_FIELD_NAMES.address_2],
      country: form?.[BILLING_ADDRESS_FIELD_NAMES.country]?.code || billingAddress[BILLING_ADDRESS_FIELD_NAMES.country]?.code,
      city: form?.[BILLING_ADDRESS_FIELD_NAMES.city] || billingAddress?.[BILLING_ADDRESS_FIELD_NAMES.city],
      state: form?.[BILLING_ADDRESS_FIELD_NAMES.state] || billingAddress?.[BILLING_ADDRESS_FIELD_NAMES.state],
      postcode: form?.[BILLING_ADDRESS_FIELD_NAMES.postcode] || billingAddress?.[BILLING_ADDRESS_FIELD_NAMES.postcode],
    };

    const _billingAddress = {
      name: form?.[BILLING_ADDRESS_FIELD_NAMES.billing_name] ||
        billingAddress?.[BILLING_ADDRESS_FIELD_NAMES.billing_name] ||
        billingAddress?.[BILLING_ADDRESS_FIELD_NAMES.name],
      phone_number: phoneNumber,
      address_1: form?.[BILLING_ADDRESS_FIELD_NAMES.billing_address_1] ||
        billingAddress?.[BILLING_ADDRESS_FIELD_NAMES.billing_address_1] ||
        billingAddress?.[BILLING_ADDRESS_FIELD_NAMES.address_1],
      address_2: form?.[BILLING_ADDRESS_FIELD_NAMES.billing_address_2] ||
        billingAddress?.[BILLING_ADDRESS_FIELD_NAMES.billing_address_2] ||
        billingAddress?.[BILLING_ADDRESS_FIELD_NAMES.address_2],
      country: form?.[BILLING_ADDRESS_FIELD_NAMES.country]?.code ||
        billingAddress[BILLING_ADDRESS_FIELD_NAMES.country]?.code ||
        billingAddress[BILLING_ADDRESS_FIELD_NAMES.billing_country]?.code,
      city: form?.[BILLING_ADDRESS_FIELD_NAMES.billing_city] ||
        billingAddress?.[BILLING_ADDRESS_FIELD_NAMES.billing_city] ||
        billingAddress?.[BILLING_ADDRESS_FIELD_NAMES.city],
      state: form?.[BILLING_ADDRESS_FIELD_NAMES.billing_state] ||
        billingAddress?.[BILLING_ADDRESS_FIELD_NAMES.billing_state] ||
        billingAddress?.[BILLING_ADDRESS_FIELD_NAMES.state],
      postcode: form?.[BILLING_ADDRESS_FIELD_NAMES.billing_postcode] ||
        billingAddress?.[BILLING_ADDRESS_FIELD_NAMES.billing_postcode] ||
        billingAddress?.[BILLING_ADDRESS_FIELD_NAMES.postcode],
    };

    const _shippingMethod = form?.[BILLING_ADDRESS_FIELD_NAMES.shippingMethod] ||
      billingAddress?.[BILLING_ADDRESS_FIELD_NAMES.shippingMethod];

    try {
      const data = await POST_PREVIEW_STRIPE_TERMINAL_ORDER(
        storeId,
        products,
        _address,
        comments,
        isAnonymous,
        _billingAddress,
        _shippingMethod,
        userPaysFees,
      );

      dispatch(setCanPreviewStripeTerminalOrder(true));
      dispatch(setShippingAddressDisabledState(true));

      return data;
    } catch (error) {
      dispatch(showErrorMessage({ message: error.response?.data?.message }))
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

const setShopperDetails = (state, { payload }) => {
  state.loading = false;
  if (payload.name) {
    state.shopperDetails = payload;
  } else {
    state.verificationCodeId = null;
    state.shopperDetails = {};
    state.shippingDetails = {};
  }
};

export const setShippingAddressDisabledState = createAction('disable-shipping-address');

export const setCanPreviewStripeTerminalOrder = createAction('allow-preview-of-stripe-terminal-order');

export const checkoutStripePublishableApiKeySelector = state => state.checkout?.store?.company?.data?.integration?.data?.stripe_publishable_api_key;

export const checkoutStoreIntegrationSelector = state => state.checkout?.store?.company?.data?.integration?.data.type;

export const getStoreIntegration = store => store?.data?.company?.data?.integration?.data.type;

const checkoutSlice = createSlice({
  name: CHECKOUT,
  initialState,
  reducers: {
    setCheckoutStep: (state, { payload }) => {
      state.checkoutStep = payload;
    },
    setCheckoutUrl: (state, { payload }) => {
      state.url = payload;
    },
    setCheckoutError: (state, { payload }) => {
      state.error = payload || PAYMENT_TEXT.defaultError;
    },
    clearOrderCreate: (state) => {
      state.error = null;
      state.orderCreateResponse = {};
    },
    setRenderStripeForm: (state, { payload }) => {
      state.renderStripeForm = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrder.rejected, (state, action) => {
        if (action.meta.arg.token && state.url) {
          state.error = PAYMENT_TEXT.defaultError;
        }
      })
      .addCase(cleanCart.fulfilled, (state) => {
        return (state = initialState);
      })
      .addCase(setShippingAddressDisabledState, (state, { payload }) => {
        state.shippingAddressDisabled = payload;
      })
      .addCase(setCanPreviewStripeTerminalOrder, (state, { payload }) => {
        state.canPreviewStripeTerminalOrder = payload;
      })
      .addCase(getStoreById.fulfilled, (state, { payload }) => {
        state.store = payload;
      })
      .addCase(createOrder.fulfilled, (state, { payload }) => {
        state.orderCreateResponse = payload;
      })
      .addCase(createOrder.rejected, (state, { payload }) => {
        state.error = payload.message || PAYMENT_TEXT.defaultError;
      })
      .addCase(getCheckoutPaymentMethods.fulfilled, (state, { payload }) => {
        state.checkoutPaymentMethods = payload;
      })
      .addCase(changeShopper.fulfilled, (state) => {
        state.shopperDetails = {};
        state.shippingDetails = {};
        state.verificationCodeId = null;
      })
      .addCase(addDeliveryAddress.fulfilled, (state, { payload }) => {
        if (!!payload) {
          state.shippingDetails = payload;
        }
        state.verificationCodeId = null;
      })
      .addCase(getDeliveryAddress.fulfilled, (state, { payload }) => {
        if (!!payload.length) {
          state.shippingDetails = payload[payload.length - 1];
        } else {
          state.shippingDetails = {};
        }
        state.verificationCodeId = null;
      })
      .addCase(requestVerificationCode.fulfilled, (state, { payload }) => {
        state.verificationCodeId = payload;
      })
      .addCase(validateVerificationCode.fulfilled, (state, { payload }) => {
        state.verificationCodeId = null;
        state.shopperDetails.phone_verified = true;
      })
      .addCase(addShopperDetails.fulfilled, setShopperDetails)
      .addCase(addShopperDetails.pending, (state, { payload }) => {
        state.loading = true;
      })
      .addCase(getShopperDetails.fulfilled, setShopperDetails)
      .addCase(addShopperDetails.rejected, (state, { payload }) => {
        state.shopperDetails = {};
        state.loading = false;
      })
      .addCase(getShopperDetails.rejected, (state, { payload }) => {
        state.shopperDetails = {};
      })
      .addCase(previewStripeTerminalOrder.fulfilled, (state, { payload }) => {
        state.stripeTerminalPreview = payload;
        state.previewingTerminalOrder = false;
      })
      .addCase(previewStripeTerminalOrder.pending, (state, { payload }) => {
        state.previewingTerminalOrder = true;
      })
      .addCase(previewStripeTerminalOrder.rejected, (state, { payload }) => {
        state.previewingTerminalOrder = false;
        state.canPreviewStripeTerminalOrder = false;
      });
  },
});

export const {
  setCheckoutStep,
  setCheckoutError,
  clearOrderCreate,
  setCheckoutUrl,
  setRenderStripeForm,
} = checkoutSlice.actions;

export default checkoutSlice.reducer;
